import { useMutation, useQuery } from '@apollo/client';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import UPDATE_CURRENT_TENANT_DEFAULT_CURRENCY from 'api/mutations/UPDATE_CURRENT_TENANT_DEFAULT_CURRENCY';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import Button from 'layout/atoms/Button/Button';
import ConfirmationModal from 'layout/molecules/ConfirmationModal';
import CurrencySelect from 'layout/molecules/CurrencySelect';
import FormField from 'layout/molecules/FormField/FormField';
import Grid from 'molecules/Grid/Grid';
import { closeModal, openModal, selectModal } from 'store/layoutSlice';
import { cleanData, loadOneSpecificationData, loadTestData, selectMaintenanceStatus, } from 'store/parameters/maintenanceSlice';
import { addToast } from 'store/toastsSlice';
function Maintenance() {
    var _a, _b;
    const dispatch = useDispatch();
    const { t } = useTranslation(['common']);
    const modal = useSelector(selectModal);
    const status = useSelector(selectMaintenanceStatus);
    const { data: tenantData } = useQuery(GET_CURRENT_TENANT);
    const [updateCurrentTenantDefaultCurrency, { loading: updateDefaultCurrencyLoading }] = useMutation(UPDATE_CURRENT_TENANT_DEFAULT_CURRENCY, {
        refetchQueries: [{ query: GET_CURRENT_TENANT }],
        awaitRefetchQueries: true,
    });
    const defaultCurrency = (_b = (_a = tenantData === null || tenantData === void 0 ? void 0 : tenantData.me) === null || _a === void 0 ? void 0 : _a.currentTenant) === null || _b === void 0 ? void 0 : _b.defaultCurrency;
    const handleClean = useCallback((s) => dispatch(cleanData({ runSeed: s }))
        .unwrap()
        .then(() => {
        dispatch(closeModal(s ? 'confirmCleanAndSeed' : 'confirmClean'));
        dispatch(addToast({
            color: 'positive',
            title: t(s ? 'clean_and_seed_success' : 'clean_success'),
        }));
    }), [dispatch, t]);
    const handleLoadTestData = useCallback(() => dispatch(loadTestData())
        .unwrap()
        .then(() => {
        dispatch(closeModal({ name: 'confirmLoadTestData' }));
        dispatch(addToast({ color: 'positive', title: t('load_test_data_success') }));
    }), [dispatch, t]);
    const handleLoadSpecificationData = useCallback(() => dispatch(loadOneSpecificationData())
        .unwrap()
        .then(() => {
        dispatch(closeModal({ name: 'confirmLoadSpecificationData' }));
        dispatch(addToast({ color: 'positive', title: t('load_single_specification_data_success') }));
    }), [dispatch, t]);
    const handleDefaultCurrencyChange = useCallback((currencyId, isoCode) => {
        if (currencyId === (defaultCurrency === null || defaultCurrency === void 0 ? void 0 : defaultCurrency.id)) {
            return;
        }
        updateCurrentTenantDefaultCurrency({ variables: { currencyId } }).then(() => {
            dispatch(addToast({
                color: 'positive',
                title: t('default_currency_changed', { currency: isoCode.toUpperCase() }),
            }));
        });
    }, [defaultCurrency === null || defaultCurrency === void 0 ? void 0 : defaultCurrency.id, updateCurrentTenantDefaultCurrency, dispatch, t]);
    return (React.createElement(Grid, { gap: 24 },
        React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                gap: '16px',
                maxWidth: '280px',
                margin: '0 auto',
            } },
            React.createElement(FormField, { label: t('choose_default_currency') },
                React.createElement(CurrencySelect, { size: "m", value: (defaultCurrency === null || defaultCurrency === void 0 ? void 0 : defaultCurrency.isoCode) || '', onChange: handleDefaultCurrencyChange, disabled: updateDefaultCurrencyLoading, useDefaultCurrencyFallback: false })),
            React.createElement(Button, { color: "primary", disabled: true, onClick: () => dispatch(openModal({ name: 'confirmClean' })) }, t('clean')),
            React.createElement(Button, { color: "primary", disabled: true, onClick: () => dispatch(openModal({ name: 'confirmCleanAndSeed' })) }, t('clean_and_seed')),
            React.createElement(Button, { color: "primary", disabled: true, onClick: () => dispatch(openModal({ name: 'confirmLoadTestData' })) }, t('load_test_data')),
            React.createElement(Button, { color: "primary", disabled: true, onClick: () => dispatch(openModal({ name: 'confirmLoadSpecificationData' })) }, t('load_single_specification_data'))),
        modal.some((modal) => modal.name === 'confirmClean') && (React.createElement(ConfirmationModal, { name: "confirmClean", header: t('clean'), text: t('confirm_clean'), action: () => handleClean(false), loading: status.clean })),
        modal.some((modal) => modal.name === 'confirmCleanAndSeed') && (React.createElement(ConfirmationModal, { name: "confirmCleanAndSeed", header: t('clean_and_seed'), text: `${t('confirm_clean')} ${t('confirm_seed')}`, action: () => handleClean(true), loading: status.clean })),
        modal.some((modal) => modal.name === 'confirmLoadTestData') && (React.createElement(ConfirmationModal, { name: "confirmLoadTestData", header: t('load_test_data'), text: t('confirm_load_test_data'), action: handleLoadTestData, loading: status.loadTestData })),
        modal.some((modal) => modal.name === 'confirmLoadSpecificationData') && (React.createElement(ConfirmationModal, { name: "confirmLoadSpecificationData", header: t('load_single_specification_data'), text: t('confirm_load_test_data'), action: handleLoadSpecificationData, loading: status.loadOneSpecificationData }))));
}
export default memo(Maintenance);
